import type { SiteData } from '@/types/site';
import React, { useMemo, useState } from 'react';

export function useSorting(sites: SiteData[]) {
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // 정렬 처리 함수
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // 정렬된 사이트 데이터 (기본 필드로만 정렬)
  const sortedSites = useMemo(() => {
    if (!sortField) return sites;

    return [...sites].sort((a, b) => {
      let aValue: string | number | Date;
      let bValue: string | number | Date;

      switch (sortField) {
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'address':
          aValue = a.address;
          bValue = b.address;
          break;
        case 'motorOperation':
          aValue = a.motorOperation;
          bValue = b.motorOperation;
          break;
        case 'waterLevel':
          // 기본 waterLevel 값으로 정렬 (comprehensive status 없이)
          aValue = a.waterLevel;
          bValue = b.waterLevel;
          break;
        case 'cumulativeFlow':
          aValue = parseFloat(a.cumulativeFlow.replace(/[^\d.]/g, ''));
          bValue = parseFloat(b.cumulativeFlow.replace(/[^\d.]/g, ''));
          break;
        case 'installationDate':
          aValue = new Date(a.installationDate);
          bValue = new Date(b.installationDate);
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [sites, sortField, sortDirection]);

  // 정렬 아이콘 렌더링
  const renderSortIcon = (field: string) => {
    if (sortField !== field) {
      return React.createElement('i', {
        className: 'ri-arrow-up-down-line text-gray-400 w-4 h-4',
      });
    }
    return sortDirection === 'asc'
      ? React.createElement('i', {
          className: 'ri-arrow-up-line text-blue-600 w-4 h-4',
        })
      : React.createElement('i', {
          className: 'ri-arrow-down-line text-blue-600 w-4 h-4',
        });
  };

  return {
    sortedSites,
    handleSort,
    renderSortIcon,
  };
}
